import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEvents } from '../../../hooks/useEvents';
import { formatDate } from '../../../utils/dateFormatter';
import { useToast } from '../../../hooks/useToast';
import { AdminLayout } from '../../../components/layout/AdminLayout';

const EventsList: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('all');
  const { events, loading, error, removeEvent } = useEvents({
    upcoming: filter === 'upcoming',
    past: filter === 'past',
    is_active: true
  });
  const { showToast } = useToast();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ? Cette action est irréversible.')) {
      try {
        setDeletingId(id);
        await removeEvent(id);
        showToast('Événement supprimé avec succès', 'success');
      } catch (error) {
        showToast('Erreur lors de la suppression de l\'événement', 'error');
      } finally {
        setDeletingId(null);
      }
    }
  };
  
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Gestion des événements</h1>
            <p className="text-gray-300">Créez et gérez les événements liés à vos concours ou indépendants.</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link
              to="/events/admin/create"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-yellow-400 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Nouvel événement
            </Link>
          </div>
        </div>

        
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-md shadow-sm bg-white/5 border border-white/10" role="group">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                filter === 'all'
                  ? 'bg-yellow-400 text-black'
                  : 'bg-white/10 text-gray-200 hover:bg-white/20'
              }`}
            >
              Tous
            </button>
            <button
              type="button"
              onClick={() => setFilter('upcoming')}
              className={`px-4 py-2 text-sm font-medium ${
                filter === 'upcoming'
                  ? 'bg-yellow-400 text-black'
                  : 'bg-white/10 text-gray-200 hover:bg-white/20'
              }`}
            >
              À venir
            </button>
            <button
              type="button"
              onClick={() => setFilter('past')}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                filter === 'past'
                  ? 'bg-yellow-400 text-black'
                  : 'bg-white/10 text-gray-200 hover:bg-white/20'
              }`}
            >
              Passés
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  Une erreur est survenue lors du chargement des événements.
                </p>
              </div>
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-black/90 shadow rounded-lg p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            <h3 className="mt-2 text-sm font-medium text-yellow-400">Aucun événement</h3>
            <p className="mt-1 text-sm text-gray-300">
              Commencez par créer un nouvel événement.
            </p>
            <div className="mt-6">
              <Link
                to="/admin/events/create"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-400 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Nouvel événement
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-black/90 shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {events.map((event, index) => {
                const isUpcoming = new Date(event.start_date) > new Date();
                
                return (
                  <li key={event.id} className={index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {event.image_url ? (
                            <img
                              src={event.image_url}
                              alt={event.title}
                              className="h-12 w-12 rounded-md object-cover mr-4 border border-white/10"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-md bg-white/5 border border-white/10 flex items-center justify-center mr-4">
                              <svg className="h-6 w-6 text-yellow-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                              </svg>
                            </div>
                          )}
                          <div>
                            <h3 className="text-lg font-medium text-gray-100 truncate max-w-md">
                              {event.title}
                            </h3>
                            <div className="mt-1 flex items-center text-sm text-gray-300">
                              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-yellow-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                              </svg>
                              <span>{formatDate(event.start_date.toString())}</span>
                              {event.end_date && (
                                <span> - {formatDate(event.end_date.toString())}</span>
                              )}
                            </div>
                            {event.location && (
                              <div className="mt-1 flex items-center text-sm text-gray-300">
                                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-yellow-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>
                                <span>{event.location}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center">
                          {isUpcoming ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-500/20 text-green-400">
                              À venir
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-500/20 text-gray-400">
                              Passé
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-4 flex flex-wrap gap-2 justify-end">
                        <Link
                          to={`/events/admin/${event.id}/tickets`}
                          className="inline-flex items-center px-3 py-1.5 border border-white/10 text-sm leading-5 font-medium rounded-md text-gray-200 bg-white/5 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
                        >
                          <svg className="-ml-1 mr-1 h-5 w-5 text-yellow-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
                          </svg>
                          Tickets
                        </Link>
                        <Link
                          to={`/events/admin/${event.id}/attendees`}
                          className="inline-flex items-center px-3 py-1.5 border border-white/10 text-sm leading-5 font-medium rounded-md text-gray-200 bg-white/5 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
                        >
                          <svg className="-ml-1 mr-1 h-5 w-5 text-yellow-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                          </svg>
                          Participants
                        </Link>
                        <Link
                          to={`/events/admin/${event.id}/edit`}
                          className="inline-flex items-center px-3 py-1.5 border border-white/10 text-sm leading-5 font-medium rounded-md text-gray-200 bg-white/5 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
                        >
                          <svg className="-ml-1 mr-1 h-5 w-5 text-yellow-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                          </svg>
                          Modifier
                        </Link>
                        <button
                          onClick={() => handleDelete(event.id)}
                          disabled={deletingId === event.id}
                          className="inline-flex items-center px-3 py-1.5 border border-red-500/30 text-sm leading-5 font-medium rounded-md text-red-400 bg-red-500/10 hover:bg-red-500/20 focus:outline-none focus:ring-2 focus:ring-red-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingId === event.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-400 mr-1"></div>
                          ) : (
                            <svg className="-ml-1 mr-1 h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                          )}
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default EventsList;
